// components/onboarding/OnboardingLauncher.jsx
//
// Persistent, non-dismissible "Finish onboarding" launcher (bottom-right). Complements the
// dismissible reminder banner (see ONBOARDING_UX_REDESIGN.md §5): even after the banner is
// dismissed for the session, this always-visible entry point guarantees the merchant can get
// back to the Onboarding Hub. Renders nothing once business ops are unlocked, while loading,
// or when already on the Hub.

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useOnboardingProgress from '../../hooks/useOnboardingProgress';

export default function OnboardingLauncher() {
  const navigate = useNavigate();
  const location = useLocation();
  const { progress, businessOpsUnlocked, completionPercent, status } = useOnboardingProgress();

  if (!progress || businessOpsUnlocked !== false) return null;
  // Submitted (under review) accounts can place orders — hide the launcher, nothing to finish.
  if (status === 'PENDING_VERIFICATION') return null;
  if (location.pathname === '/onboarding') return null;

  const pct = Math.max(0, Math.min(100, completionPercent || 0));
  const urgent = status === 'REJECTED' || status === 'CHANGE_REQUESTED' || (progress.agreementRequired && !progress.agreementSigned);
  const label =
    status === 'PENDING_VERIFICATION' ? 'Onboarding under review'
    : urgent ? 'Action needed'
    : 'Finish onboarding';

  return (
    <button
      type="button"
      onClick={() => navigate('/onboarding')}
      aria-label="Finish onboarding"
      className={`fixed bottom-5 left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full py-2.5 pl-3 pr-4 text-white shadow-xl transition-transform hover:scale-[1.03] active:scale-95 ${
        urgent ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-blue-600 to-indigo-600'
      }`}
    >
      <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-sm font-bold">
        {status === 'PENDING_VERIFICATION' ? '⏳' : `${pct}%`}
        {!urgent && status !== 'PENDING_VERIFICATION' && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/20" />
        )}
      </span>
      <span className="text-sm font-semibold whitespace-nowrap">{label}</span>
    </button>
  );
}
